import * as React from 'react';
import { Component, Fragment } from 'react';
import { inject, observer } from 'mobx-react';
import { computed } from 'mobx';
import { withTheme } from 'styled-components';
import DownloadControls from './Document/DownloadControls';
import WatchListToggle from './WatchListToggle';
import ChangeIndicator from './ChangeIndicator';
import StarRating from './StarRating';
import { ShareclassColumn } from './ShareclassRows';
import Icon from './Icon';
import Translate from './Translate';
import DynamicLink from './DynamicLink';
import { DataStore, HistoryStore } from '../stores';
import InlineDisclaimers from './InlineDisclaimers';
import DocumentMenu from './Document/DocumentMenu';
import { camelCase } from '@kurtosys/ksys-app-template/dist/utils/changeCase';
import { isArrayLike } from '@kurtosys/ksys-app-template/dist/utils/isArrayLike';

const starIconStyleProps = {
    height: '1.2em',
    width: '1.2em',
};

interface ShareclassColumnValueProps {
    dataStore?: DataStore;
    historyStore?: HistoryStore;
    // TODO: Setup all available props
    [key: string]: any;
}

@inject('dataStore', 'historyStore')
@observer
class ShareclassColumnValue extends Component<ShareclassColumnValueProps> {
    @computed
    get selectedGroupIndex() {
        let index = this.props.selectedGroupMap.get(this.props.columnIndex);
        if (this.props.column.groups) {
            if (index >= this.props.column.groups.length) {
                const selectedConfigGroupIndex = this.props.column.groups.findIndex(
                    c => c.selected
                );
                // tslint:disable-next-line: prefer-conditional-expression
                if (selectedConfigGroupIndex > -1) {
                    index = selectedConfigGroupIndex;
                } else {
                    index = 0;
                }
            }
        }
        return index;
    }

    @computed
    get column() {
        return this.props.column.groups
            ? this.props.column.groups[this.selectedGroupIndex]
            : this.props.column;
    }
    getGroupOrDropdownColumnContent = column => {
        const { theme, isShareclassDisabled = false } = this.props;
        const shareclassColumnGroupedTheme = isShareclassDisabled
            ? theme.ShareclassColumnGroupedDisabled
            : theme.ShareclassColumnGrouped;

        return (
            column.values &&
            column.values.map((childColumn, cIndex) => {
                return (
                    <ShareclassColumn
                        key={`${column.type}-${childColumn.value}-${cIndex}`}
                        paddingX="sm"
                        paddingY="0"
                        textAlign={childColumn.textAlign || 'center'}
                        width={childColumn.width}
                        whiteSpace={childColumn.wrapText ? 'normal' : 'nowrap'}
                        {...shareclassColumnGroupedTheme}
                    >
                        {this.getColumnContent(childColumn)}
                    </ShareclassColumn>
                );
            })
        );
    };

    getDownloadColumnContent = column => {
        const { theme, dataStore, shareClass } = this.props;
        const { downloadCount = 0, shareClassDownloads } = shareClass;
        return (
            <DownloadControls
                count={downloadCount}
                display="inline-flex"
                disabled={downloadCount === 0}
                showPreview={
                    dataStore.config.documentPreviewEnabled &&
                    downloadCount === 1
                }
                theme={{
                    ...theme,
                    ...{
                        DownloadButton: theme.ShareclassRowDownloadButton,
                        DownloadButtonIcon:
                            theme.ShareclassRowDownloadButtonIcon,
                        PreviewButtonIcon: theme.ShareclassRowPreviewButtonIcon,
                        DownloadCount: theme.ShareclassRowDownloadCount,
                    },
                }}
                onClick={e => {
                    e.stopPropagation();
                    dataStore.downloadDocuments(shareClassDownloads);
                }}
                href={dataStore.getDocumentLink(shareClassDownloads)}
                onPreviewClick={e => {
                    e.stopPropagation();
                    dataStore.previewDocuments(shareClassDownloads);
                }}
                fileName={dataStore.getFileName(shareClassDownloads)}
                previewButtonFileName={dataStore.config.documentPreviewIcon}
                metricContext={`shareclassColumn-${camelCase(
                    column.label
                )}-${camelCase(shareClass.shareClass.clientCode)}`}
            />
        );
    };

    @computed
    get watchlistColumnContent() {
        const { shareClass } = this.props;
        return (
            <WatchListToggle
                onClick={e => {
                    e.stopPropagation();
                    shareClass.toggleWatchList();
                }}
                active={shareClass.inWatchList}
                accessibilityContext={`for share class identified by ${shareClass.shareClass.clientCode}`}
                metricContext={camelCase(shareClass.shareClass.clientCode)}
            />
        );
    }
    getDocumentColumnContent = column => {
        const { theme, dataStore, shareClass } = this.props;
        const documents = column.value;

        if (Array.isArray(column.value) && documents.length > 0) {
            return (
                <DocumentMenu
                    documents={documents}
                    documentSelected={shareClass.documentSelected}
                    toggleDownload={shareClass.toggleDownload}
                    showCount={false}
                    showSingleDocument={column.showSingleDocument}
                    showSingleDocumentLabel={column.showSingleDocumentLabel}
                    entityWrapper={shareClass}
                    theme={{
                        ...theme,
                        ...{
                            DownloadButtonIconNoLabel: {
                                margin: '0 auto',
                            },
                            DownloadControls: column.showSingleDocument &&
                                documents.length === 1 && {
                                    justifyContent: 'center',
                                },
                        },
                    }}
                    metricContext={`shareclassColumn-${camelCase(
                        shareClass.shareClass.clientCode
                    )}`}
                >
                    <Icon
                        fileName="DocumentIcon"
                        height="1.4em"
                        width="1.4em"
                        {...theme.ShareclassRowDocumentIcon}
                    />
                </DocumentMenu>
            );
        }
        return (
            <Translate>
                {column.noDataPlaceholder || dataStore.config.noDataPlaceholder}
            </Translate>
        );
    };
    getPricesColumnContent = column => {
        const { historyStore, shareClass } = this.props;
        if (column.data.performanceCheck && !shareClass.hasPerformance) {
            return <Translate>{column.data.value}</Translate>;
        } else {
            return (
                <DownloadControls
                    display="inline-flex"
                    onClick={e => {
                        e.stopPropagation();
                        historyStore.getPriceHistory(
                            column,
                            shareClass.shareClass.clientCode
                        );
                    }}
                    count={1}
                    showCount={false}
                    // Due to historic prices building up filename on the response, we can not accurately set this. Default to historic_prices.csv
                    fileName={'historic_prices.csv'}
                    metricContext={`shareclassColumn-${camelCase(
                        column.label
                    )}-${camelCase(shareClass.shareClass.clientCode)}`}
                />
            );
        }
    };
    getStarsColumnContent = column => {
        const { theme, shareClass } = this.props;
        if (column.data.performanceCheck && !shareClass.hasPerformance) {
            return <Translate>{column.data.value}</Translate>;
        } else {
            return (
                <StarRating
                    rating={column.data.value}
                    noDataPlaceholder={column.noDataPlaceholder}
                    showEmptyStars={column.type === 'starsOutOf'}
                    display="block"
                    theme={{
                        ...theme,
                        ...theme.StarRatingColumnWrapper,
                        StarIcon: {
                            ...starIconStyleProps,
                            ...theme.StarIconColumn,
                        },
                        StarOutlineIcon: {
                            ...starIconStyleProps,
                            ...theme.StarOutlineIconColumn,
                        },
                    }}
                />
            );
        }
    };
    getSimpleColumnContent = (column, data, key: any = undefined) => {
        const { dataStore } = this.props;
        const separator = column.multiple && data.separator;
        return (
            <Fragment key={key}>
                {data.showChange ? (
                    <ChangeIndicator
                        dataStore={dataStore}
                        showChange={data.showChange}
                        value={data.value}
                        separator={separator}
                    />
                ) : (
                    <Fragment>
                        <Translate>{data.value}</Translate>
                        {separator || null}
                    </Fragment>
                )}
            </Fragment>
        );
    };
    getColumnReplacementValue(column): string {
        const dataRecords = isArrayLike(column.data)
            ? column.data
            : [column.data];
        let value = '';
        dataRecords.forEach(data => {
            value += `${data.value}${
                column.multiple && data.separator ? data.separator : ''
            }`;
        });
        return value;
    }
    getColumnContent = column => {
        const { theme, dataStore, shareClass, queryContext } = this.props;

        let columnContent;
        // Determine which column types can be wrapper with an anchor
        let hasLink = false;
        if (['group', 'dropdown'].includes(column.type)) {
            hasLink = true;
            columnContent = this.getGroupOrDropdownColumnContent(column);
        } else if (column.type === 'download') {
            columnContent = this.getDownloadColumnContent(column);
        } else if (column.type === 'watchlist' && dataStore.watchListEnabled) {
            columnContent = this.watchlistColumnContent;
        } else if (column.type === 'document') {
            columnContent = this.getDocumentColumnContent(column);
        } else if (column.type === 'prices') {
            columnContent = this.getPricesColumnContent(column);
        } else if (column.type === 'stars' || column.type === 'starsOutOf') {
            columnContent = this.getStarsColumnContent(column);
        } else if (column.multiple) {
            hasLink = true;
            columnContent = (
                <Fragment>
                    {column.data.map((data, i) =>
                        this.getSimpleColumnContent(column, data, i)
                    )}
                </Fragment>
            );
        } else {
            hasLink = true;
            columnContent = this.getSimpleColumnContent(column, column.data);
        }

        if (hasLink && column.url) {
            return (
                <InlineDisclaimers
                    conditionalDisclaimers={column.cellDisclaimers}
                    entityWrapper={shareClass}
                >
                    <DynamicLink
                        config={column}
                        shareClass={shareClass.shareClass}
                        dataStore={dataStore}
                        queryContext={queryContext}
                        metricContext={`shareclassColumn-${camelCase(
                            column.label
                        )}-${camelCase(shareClass.shareClass.clientCode)}`}
                        ariaLabelKey={'shareClassRowLinks'}
                        ariaLabelReplacements={{
                            label: column.label,
                            value: this.getColumnReplacementValue(column),
                        }}
                        {...theme.ShareclassColumnLink}
                    >
                        {columnContent}
                    </DynamicLink>
                </InlineDisclaimers>
            );
        }

        return (
            <InlineDisclaimers
                conditionalDisclaimers={column.cellDisclaimers}
                entityWrapper={shareClass}
            >
                {columnContent}
            </InlineDisclaimers>
        );
    };
    render() {
        return this.getColumnContent(this.column);
    }
}

export default withTheme(ShareclassColumnValue) as any;



// WEBPACK FOOTER //
// ./src/Components/ShareclassColumnValue.tsx